import { SearchIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useMemo, useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { deleteBusinessCard, uploadBusinessCard } from '@/api/businessCards'
import { Salutation, type Contact, type Customer } from '@/api/customers'
import {
  captureLead,
  LeadStatus,
  updateLead,
  type Lead,
  type LeadContactInput,
  type LeadCustomerInput,
} from '@/api/leads'
import { isRefusal, useApiErrorMessage } from '@/api/errors'
import { Role } from '@/auth/roles'
import { useAuth } from '@/auth/useAuth'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { DateField } from '@/components/layout/DateField'
import { FieldGrid, FormSection, FullWidth } from '@/components/layout/FormSection'
import { AssigneeName } from '@/components/leads/AssigneeName'
import { AssignLeadDialog } from '@/components/leads/AssignLeadDialog'
import { BusinessCardStrip } from '@/components/leads/BusinessCardStrip'
import { ContactFields } from '@/components/leads/ContactFields'
import { CountrySelect } from '@/components/leads/CountrySelect'
import { LeadNotes } from '@/components/leads/LeadNotes'
import { CustomerMatchHint } from '@/components/leads/CustomerMatchHint'
import { CustomerPicker } from '@/components/leads/CustomerPicker'
import { Field } from '@/components/layout/Field'

import { InterestPicker } from '@/components/leads/InterestPicker'
import {
  hasPendingCards,
  NO_PENDING_CARDS,
  type PendingCards,
} from '@/components/leads/pendingCards'
import { TradeFairSelect } from '@/components/leads/TradeFairSelect'
import { useColleagueDirectory } from '@/hooks/useColleagues'
import { useProductCatalog } from '@/hooks/useProductCatalog'
import { PHONE_FULL_SCREEN_DIALOG } from '@/components/layout/dialogStyles'
import { formatDate, parseIsoDate, toIsoDate } from '@/lib/dates'
import { cn } from '@/lib/utils'

type Errors = {
  tradeFair?: string
  company?: string
  capturedOn?: string
  contacts?: Record<number, string>
}

/** Everything the form holds, in one place — which is what makes "has this changed?" answerable. */
type Values = {
  tradeFairId: string | null
  customer: LeadCustomerInput
  contacts: LeadContactInput[]
  /** The day of the conversation, as an ISO date. The time of day is kept, never edited. */
  capturedOn: string
  summary: string
  interestIds: string[]
}

/** Today, in the local calendar rather than UTC's — a stand is somewhere, not at Greenwich. */
function today(): string {
  return toIsoDate(new Date())
}

/**
 * The day back together with the time it was written at.
 *
 * Only the day is on the form, so the time of the original conversation survives being corrected
 * a week later — and a lead typed up in the evening keeps the hour it was typed, which is what
 * orders two leads captured on the same day.
 */
function capturedAtFrom(day: string, previous: string | null): string {
  const base = previous ? new Date(previous) : new Date()
  const parsed = parseIsoDate(day)
  if (!parsed || Number.isNaN(base.getTime())) {
    return base.toISOString()
  }
  parsed.setHours(base.getHours(), base.getMinutes(), base.getSeconds(), base.getMilliseconds())
  return parsed.toISOString()
}

function emptyContact(): LeadContactInput {
  return {
    salutation: Salutation.NONE,
    firstName: null,
    lastName: '',
    position: null,
    email: null,
    phone: null,
    mobile: null,
  }
}

function initialValues(lead: Lead | null): Values {
  return {
    tradeFairId: lead?.tradeFairId ?? null,
    customer: lead
      ? {
          sourceCustomerId: lead.customer.sourceCustomerId,
          companyName: lead.customer.companyName,
          customerNumber: lead.customer.customerNumber,
          address: { ...lead.customer.address },
          phone: lead.customer.phone,
          email: lead.customer.email,
          website: lead.customer.website,
        }
      : {
          companyName: '',
          customerNumber: null,
          address: { street: null, postalCode: null, city: null, country: null },
          phone: null,
          email: null,
          website: null,
        },
    contacts:
      lead && lead.customer.contacts.length > 0
        ? lead.customer.contacts.map((contact) => ({ ...contact }))
        : [emptyContact()],
    // The local day, as `capturedAtFrom` reads it back: the UTC one is the day before for anything
    // written up between midnight and two in the morning here, and every save moved it back again.
    capturedOn: lead ? toIsoDate(new Date(lead.capturedAt)) : today(),
    summary: lead?.summary ?? '',
    interestIds: (lead?.interests ?? []).map((interest) => interest.id).sort(),
  }
}

/**
 * A stable string for a set of values, so "is there anything unsaved here?" is one comparison.
 *
 * Written out field by field rather than stringifying the state object: an absent optional and an
 * explicit null are the same thing to this form and must not read as a change, and the order of
 * the interests is not one either.
 */
function fingerprint(values: Values): string {
  return JSON.stringify({
    tradeFairId: values.tradeFairId,
    customer: {
      sourceCustomerId: values.customer.sourceCustomerId ?? null,
      companyName: values.customer.companyName,
      customerNumber: values.customer.customerNumber ?? null,
      street: values.customer.address.street ?? null,
      postalCode: values.customer.address.postalCode ?? null,
      city: values.customer.address.city ?? null,
      country: values.customer.address.country ?? null,
      phone: values.customer.phone ?? null,
      email: values.customer.email ?? null,
      website: values.customer.website ?? null,
    },
    contacts: values.contacts.map((contact) => ({
      id: contact.id ?? null,
      sourceContactId: contact.sourceContactId ?? null,
      salutation: contact.salutation,
      firstName: contact.firstName ?? null,
      lastName: contact.lastName,
      position: contact.position ?? null,
      email: contact.email ?? null,
      phone: contact.phone ?? null,
      mobile: contact.mobile ?? null,
    })),
    capturedOn: values.capturedOn,
    summary: values.summary,
    interestIds: [...values.interestIds].sort(),
  })
}

/**
 * Whitespace off both ends, and an empty result is an absent value.
 *
 * The backend trims to null anyway, so without this a pasted " Meyer " would come back different
 * from what was typed — and, until the lead is reloaded, would read as an unsaved change.
 */
function trimmed(value: string | null | undefined): string | null {
  return value?.trim() || null
}

/**
 * A contact nobody typed anything into. Only ever dropped when it has no `id`: one that already
 * exists on the server has a row, and possibly business card photos hanging off it, and emptying
 * the fields must not be a way to delete those without being asked.
 */
function isEmptyContact(contact: LeadContactInput): boolean {
  return (
    !contact.lastName.trim() &&
    !contact.firstName?.trim() &&
    !contact.position?.trim() &&
    !contact.email?.trim() &&
    !contact.phone?.trim() &&
    !contact.mobile?.trim()
  )
}

export function LeadDialog({
  open,
  lead,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  /** Null captures a new lead; a lead edits that one. */
  lead: Lead | null
  onOpenChange: (open: boolean) => void
  /** Something about the lead was saved — the form, or a hand-over from inside it. */
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [dirty, setDirty] = useState(false)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  /**
   * Every way out of this dialog comes through here — the close button, Escape, a click on the
   * overlay and the Cancel button all end up in Radix's `onOpenChange`. A lead is fifteen fields
   * typed standing up at a stand; losing them to a stray click is not a risk worth taking.
   */
  function requestClose(next: boolean) {
    if (next) {
      onOpenChange(true)
    } else if (dirty) {
      setConfirmingDiscard(true)
    } else {
      onOpenChange(false)
    }
  }

  /** Saved, or deliberately thrown away: either way there is nothing left to warn about. */
  function close() {
    setDirty(false)
    setConfirmingDiscard(false)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={requestClose}>
      <DialogContent
        className={cn(
          'flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl',
          PHONE_FULL_SCREEN_DIALOG,
        )}
      >
        <DialogHeader className="shrink-0 gap-1 border-b px-6 py-4 pr-12">
          <DialogTitle>{lead ? t('leadForm.editTitle') : t('leadForm.createTitle')}</DialogTitle>
          <DialogDescription>
            {lead
              ? t('leadForm.editDescription', { date: formatDate(lead.capturedAt.slice(0, 10)) })
              : t('leadForm.createDescription')}
          </DialogDescription>
        </DialogHeader>
        {/* Keyed, so opening the dialog on another row mounts a fresh form instead of copying the
            new values over the old ones in an effect. */}
        {open && (
          <LeadForm
            key={lead?.id ?? 'new'}
            lead={lead}
            onDirtyChange={setDirty}
            onCancel={() => requestClose(false)}
            // Handing on is saved at once, not with the form, so the list behind is out of date
            // while the dialog stays open.
            onAssigned={onSaved}
            onSaved={() => {
              onSaved()
              close()
            }}
          />
        )}

        <AlertDialog open={confirmingDiscard} onOpenChange={setConfirmingDiscard}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('leadForm.discardTitle')}</AlertDialogTitle>
              <AlertDialogDescription>{t('leadForm.discardDescription')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('leadForm.discardKeep')}</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={close}>
                {t('leadForm.discardConfirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  )
}

function LeadForm({
  lead,
  onDirtyChange,
  onCancel,
  onSaved,
  onAssigned,
}: {
  lead: Lead | null
  /** Reported upwards because the guard on closing lives with the dialog, not with the form. */
  onDirtyChange: (dirty: boolean) => void
  /** Goes through the unsaved-changes guard, exactly like every other way out. */
  onCancel: () => void
  onSaved: () => void
  /** The lead was handed on — already saved, whatever becomes of the form. */
  onAssigned: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const catalog = useProductCatalog(false)
  const { hasRole, user: me } = useAuth()
  // Only a saved lead has somebody working it; a new one goes to whoever captures it.
  const directory = useColleagueDirectory(lead !== null)
  /**
   * Whom the lead is assigned to *now*. Not part of `values`: handing on goes through its own
   * endpoint and is saved the moment it is confirmed, so it is neither unsaved work nor something
   * Cancel takes back — the same as in the list, where the action comes from.
   */
  const [assigneeId, setAssigneeId] = useState(lead?.assigneeId ?? null)
  const [assigning, setAssigning] = useState(false)
  // The backend's rule, so the screen offers nothing it would refuse: seeing a lead that was handed
  // to you is not the same as being free to pass it on.
  const mayAssign = lead !== null && (hasRole(Role.ADMIN) || lead.ownerId === me?.id)

  const initial = useMemo(() => initialValues(lead), [lead])
  const [values, setValues] = useState<Values>(initial)
  /**
   * What "unchanged" means right now. State rather than a ref, because it decides what the footer
   * says and whether closing asks a question — and it does move, once, when the running fair is
   * filled in for us.
   */
  const [baseline, setBaseline] = useState(() => fingerprint(initial))

  /**
   * The card changes each contact is carrying — pictures taken, cards marked to go — keyed by the
   * row the contact sits in, which is the only key there is before the contacts exist.
   *
   * Nothing in here has touched the server: adding and removing a card are both part of the form,
   * so Cancel discards them exactly as it discards a typed-over surname.
   */
  const [pendingCards, setPendingCards] = useState<Record<number, PendingCards>>({})
  /**
   * Bumped when a save carried out only part of the card changes. The strips list what is on the
   * server, and after a half-applied save that list is out of date.
   */
  const [cardEpoch, setCardEpoch] = useState(0)

  const [picking, setPicking] = useState(false)
  /** The contact a removal is waiting on, when it is one that already exists on the server. */
  const [removing, setRemoving] = useState<number | null>(null)
  const [errors, setErrors] = useState<Errors>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // The card changes are not part of `values`, so they need saying separately: a lead somebody
  // only photographed a card for is unsaved work, and so is one where a card was taken off.
  const cardsChanged = Object.values(pendingCards).some(hasPendingCards)
  const changed = fingerprint(values) !== baseline || cardsChanged
  useEffect(() => {
    onDirtyChange(changed)
  }, [changed, onDirtyChange])

  /** For a value that does not depend on what is already in the form. */
  function patch(next: Partial<Values>) {
    setValues((current) => ({ ...current, ...next }))
  }

  /**
   * For everything that does — a field inside `customer`, one contact out of the list.
   *
   * Building those from the `values` of the render that installed the handler looks equivalent and
   * is not: two fields of the same nested object changed in one batch would both start from the
   * same stale copy, and the second would quietly undo the first. Autofill does exactly that to an
   * address.
   */
  function update(change: (current: Values) => Partial<Values>) {
    setValues((current) => ({ ...current, ...change(current) }))
  }

  function setContact(index: number, contact: LeadContactInput) {
    update((current) => ({
      contacts: current.contacts.map((existing, at) => (at === index ? contact : existing)),
    }))
  }

  function drop(index: number) {
    update((current) => {
      const next = current.contacts.filter((_, at) => at !== index)
      // The form always shows one contact; removing the last leaves an empty one to type into.
      return { contacts: next.length > 0 ? next : [emptyContact()] }
    })
    // The card changes are keyed by row, and the rows behind this one just moved up. Without
    // this, a photograph taken for the third contact reappears under the second — or is dropped
    // on save, because the row it was filed under no longer exists. The entry for the removed row
    // goes with it: a contact deleted on the server takes its cards along.
    setPendingCards((current) => {
      const moved: Record<number, PendingCards> = {}
      for (const [key, change] of Object.entries(current)) {
        const row = Number(key)
        if (row === index) {
          continue
        }
        moved[row > index ? row - 1 : row] = change
      }
      return moved
    })
    setRemoving(null)
  }

  /** A saved contact takes its business card photos with it, so it asks first. */
  function removeContact(index: number) {
    if (values.contacts[index].id === undefined) {
      drop(index)
    } else {
      setRemoving(index)
    }
  }

  /**
   * Taking a company out of the master data: everything is **copied** into the form. What the user
   * sees and confirms is what gets frozen onto the lead — `sourceCustomerId` only records where it
   * came from.
   */
  function takeFrom(picked: Customer, contact: Contact | null) {
    const customer: LeadCustomerInput = {
      sourceCustomerId: picked.id,
      companyName: picked.companyName,
      customerNumber: picked.customerNumber,
      address: { ...picked.address },
      phone: picked.phone,
      email: picked.email,
      website: picked.website,
    }
    update((current) => ({
      customer,
      contacts: contact
        ? [
            {
              // The row on the lead is kept when editing; only the provenance comes from the
              // master record.
              id: current.contacts[0]?.id,
              sourceContactId: contact.id,
              salutation: contact.salutation,
              firstName: contact.firstName,
              lastName: contact.lastName,
              position: contact.position,
              email: contact.email,
              phone: contact.phone,
              mobile: contact.mobile,
            },
            ...current.contacts.slice(1),
          ]
        : current.contacts,
    }))
    toast.success(t('leadForm.picker.taken', { name: picked.companyName }))
  }

  /**
   * A row nobody typed into **and** took no picture for.
   *
   * A photograph with no name is not an empty row: dropping it silently would throw the picture
   * away, so it counts as filled in and the surname is then asked for like anywhere else.
   */
  function emptyRow(contact: LeadContactInput, row: number): boolean {
    return isEmptyContact(contact) && !hasPendingCards(pendingCards[row] ?? NO_PENDING_CARDS)
  }

  function validate(): boolean {
    const next: Errors = {}
    if (!values.tradeFairId) {
      next.tradeFair = t('validation.required')
    }
    if (!values.customer.companyName.trim()) {
      next.company = t('validation.required')
    }
    // Cleared or half-typed. Falling back to "now" would save a day the field does not show, and
    // on an existing lead it would silently keep the old one.
    if (!parseIsoDate(values.capturedOn)) {
      next.capturedOn = t('validation.required')
    } else if (values.capturedOn !== initial.capturedOn && values.capturedOn > today()) {
      // The backend would take it as now; saying so here beats saving a day nobody picked. Only a
      // day somebody picked: a lead stored ahead of the clock before the backend refused that
      // must not become one nobody can save.
      next.capturedOn = t('leadForm.capturedOnFuture')
    }
    const contactErrors: Record<number, string> = {}
    values.contacts.forEach((contact, index) => {
      // A contact with something in it but no surname is a slip. An entirely empty *new* one is
      // fine and simply gets dropped — an entirely empty *saved* one is somebody deleting a row by
      // clearing fields, which is what the remove button is for.
      if (!contact.lastName.trim() && (contact.id !== undefined || !emptyRow(contact, index))) {
        contactErrors[index] = t('validation.required')
      }
    })
    if (Object.keys(contactErrors).length > 0) {
      next.contacts = contactErrors
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting || !validate()) {
      return
    }
    setSubmitting(true)
    setFailure(null)
    // Only the never-filled-in ones are dropped, and each survivor remembers the row it sat in:
    // the cards are addressed by position in what is *sent*, not by position in the form.
    const kept = values.contacts
      .map((contact, row) => ({ contact, row }))
      .filter(({ contact, row }) => contact.id !== undefined || !emptyRow(contact, row))
    const body = {
      tradeFairId: values.tradeFairId as string,
      customer: {
        // Left out on an update: promoting or linking owns this field, and a plain save must not
        // be able to forge or clear the trail back to the master record.
        ...(lead ? {} : { sourceCustomerId: values.customer.sourceCustomerId ?? null }),
        companyName: values.customer.companyName.trim(),
        customerNumber: trimmed(values.customer.customerNumber),
        address: {
          street: trimmed(values.customer.address.street),
          postalCode: trimmed(values.customer.address.postalCode),
          city: trimmed(values.customer.address.city),
          country: trimmed(values.customer.address.country),
        },
        phone: trimmed(values.customer.phone),
        email: trimmed(values.customer.email),
        website: trimmed(values.customer.website),
      },
      // A saved contact leaves the lead through `removeContact`, which asks first, because its
      // business card photos go with it.
      contacts: kept.map(({ contact }) => ({
          ...contact,
          firstName: trimmed(contact.firstName),
          lastName: contact.lastName.trim(),
          position: trimmed(contact.position),
          email: trimmed(contact.email),
          phone: trimmed(contact.phone),
          mobile: trimmed(contact.mobile),
        })),
      // Not on the form any more, and therefore carried through rather than decided here: an
      // edit must not quietly reset a status set elsewhere, and the field is not optional on the
      // way out — leaving it off would land on the backend's default.
      status: lead?.status ?? LeadStatus.NEW,
      capturedAt: capturedAtFrom(values.capturedOn, lead?.capturedAt ?? null),
      summary: trimmed(values.summary),
      interestIds: values.interestIds,
    }
    try {
      if (lead) {
        const saved = await updateLead(lead.id, body)
        // The lead itself is through. Moving the baseline here keeps a card that would not go
        // from reading as "none of this was saved" when the dialog stays open below.
        setBaseline(fingerprint(values))
        const leftover = await applyCardChanges(lead.id, saved, kept)
        if (leftover) {
          setPendingCards(leftover.pending)
          // What the strips listed a moment ago no longer matches the server.
          setCardEpoch((epoch) => epoch + 1)
          setFailure(leftover.message)
          return
        }
        setPendingCards({})
        toast.success(t('leadForm.updated', { name: body.customer.companyName }))
      } else {
        // The contacts the payload actually carries decide the positions the cards are addressed
        // by: an untouched empty contact row is dropped above, and a picture keyed on the row it
        // sat in would then land on somebody else.
        const cards = new Map<number, File[]>()
        kept.forEach(({ row }, position) => {
          const held = pendingCards[row]?.files ?? []
          if (held.length > 0) {
            cards.set(position, held)
          }
        })
        // One request rather than three: at a stand, each round trip is a chance to half-save a
        // conversation.
        await captureLead(body, cards)
        toast.success(t('leadForm.created', { name: body.customer.companyName }))
      }
      onSaved()
    } catch (error) {
      setFailure(errorMessage(error, t('leadForm.failed')))
    } finally {
      setSubmitting(false)
    }
  }

  /**
   * The card changes, carried out once the lead and its contacts exist.
   *
   * Removals before uploads, per contact: the two-card limit is counted on the server, so a
   * picture taking the place of one that is on its way out would otherwise be refused.
   *
   * The lead is already saved by the time this runs, so a card that will not go cannot undo that.
   * What failed stays pending and the dialog stays open, and pressing Save again retries it — the
   * update is a PUT, and repeating it changes nothing.
   *
   * @returns null when everything was applied, otherwise what is left and the first refusal.
   */
  async function applyCardChanges(
    leadId: string,
    saved: Lead,
    rows: { contact: LeadContactInput; row: number }[],
  ): Promise<{ pending: Record<number, PendingCards>; message: string } | null> {
    // A contact typed in during this edit had no id; the save has just given it one. Matched by
    // what came back that was not sent, in order — a new contact is appended on both sides.
    const sent = new Set(rows.map(({ contact }) => contact.id).filter(Boolean))
    const fresh = saved.customer.contacts.filter((contact) => !sent.has(contact.id))
    let taken = 0
    const leftover: Record<number, PendingCards> = {}
    let message: string | null = null

    for (const { contact, row } of rows) {
      const contactId = contact.id ?? fresh[taken++]?.id
      const change = pendingCards[row]
      if (!contactId || !change || !hasPendingCards(change)) {
        continue
      }
      const removedIds: string[] = []
      for (const cardId of change.removedIds) {
        try {
          await deleteBusinessCard(leadId, contactId, cardId)
        } catch (error) {
          // Already gone is the outcome that was asked for, not a failure to report.
          if (isRefusal(error, 'FILE_NOT_FOUND')) {
            continue
          }
          removedIds.push(cardId)
          message ??= errorMessage(error, t('businessCards.deleteFailed'))
        }
      }
      const files: File[] = []
      for (const file of change.files) {
        try {
          await uploadBusinessCard(leadId, contactId, file)
        } catch (error) {
          // FILE_UNSUPPORTED_TYPE is the one worth reading: it names HEIC, which is what a phone
          // produces unless it is told otherwise.
          files.push(file)
          message ??= errorMessage(error, t('businessCards.uploadFailed'))
        }
      }
      if (files.length > 0 || removedIds.length > 0) {
        leftover[row] = { files, removedIds }
      }
    }

    return message === null ? null : { pending: leftover, message }
  }

  const companyField = (key: 'customerNumber' | 'phone' | 'email' | 'website') => ({
    id: `company-${key}`,
    value: values.customer[key] ?? '',
    onChange: (event: { target: { value: string } }) =>
      update((current) => ({
        customer: { ...current.customer, [key]: event.target.value || null },
      })),
  })

  const addressField = (key: 'street' | 'postalCode' | 'city' | 'country') => ({
    id: `address-${key}`,
    value: values.customer.address[key] ?? '',
    onChange: (event: { target: { value: string } }) =>
      update((current) => ({
        customer: {
          ...current.customer,
          address: { ...current.customer.address, [key]: event.target.value || null },
        },
      })),
  })

  const singleEmptyContact =
    values.contacts.length === 1 && values.contacts[0].id === undefined

  return (
    <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
      <div className="grid min-h-0 flex-1 gap-8 overflow-y-auto px-6 py-5">
        {failure && (
          <Alert variant="destructive">
            <AlertDescription>{failure}</AlertDescription>
          </Alert>
        )}

        <FormSection
          title={t('leadForm.tradeFairSection')}
          description={t('leadForm.tradeFairSectionHint')}
        >
          <FieldGrid>
            <Field
              id="lead-trade-fair"
              label={t('leadForm.tradeFair')}
              required
              error={errors.tradeFair}
              hint={lead ? undefined : t('leadForm.tradeFairHint')}
            >
              <TradeFairSelect
                id="lead-trade-fair"
                value={values.tradeFairId}
                onChange={(id) => patch({ tradeFairId: id })}
                onPreselect={(id) => {
                  // The app filling the field in is not the user changing it: the baseline moves
                  // with it, so an untouched dialog still closes without a question.
                  setBaseline(fingerprint({ ...initial, tradeFairId: id }))
                  patch({ tradeFairId: id })
                }}
                preselectRunning={lead === null}
                invalid={Boolean(errors.tradeFair)}
                describedBy={
                  errors.tradeFair
                    ? 'lead-trade-fair-error'
                    : lead
                      ? undefined
                      : 'lead-trade-fair-hint'
                }
              />
            </Field>

            <Field
              id="lead-captured-on"
              label={t('leadForm.capturedOn')}
              hint={t('leadForm.capturedOnHint')}
              error={errors.capturedOn}
            >
              {/* Only the day: the time of the conversation is kept as it was, which is what
                  orders two leads captured on the same day. */}
              <DateField
                id="lead-captured-on"
                value={values.capturedOn}
                invalid={Boolean(errors.capturedOn)}
                describedBy={errors.capturedOn ? 'lead-captured-on-error' : 'lead-captured-on-hint'}
                onChange={(capturedOn) => patch({ capturedOn })}
              />
            </Field>
          </FieldGrid>
        </FormSection>

        <FormSection
          title={t('leadForm.companySection')}
          description={t('leadForm.companySectionHint')}
          action={
            <Button type="button" variant="outline" size="sm" onClick={() => setPicking(true)}>
              <SearchIcon />
              {t('leadForm.picker.open')}
            </Button>
          }
        >
          <FieldGrid>
            <FullWidth>
              <Field
                id="company-name"
                label={t('leadForm.company')}
                required
                error={errors.company}
              >
                <Input
                  id="company-name"
                  value={values.customer.companyName}
                  aria-invalid={Boolean(errors.company)}
                  aria-describedby={errors.company ? 'company-name-error' : undefined}
                  onChange={(event) =>
                    update((current) => ({
                      customer: { ...current.customer, companyName: event.target.value },
                    }))
                  }
                />
                {/* Under the field it is about, and only while the company was typed in free:
                    once it came out of the master data there is nothing left to point at. */}
                <CustomerMatchHint
                  companyName={values.customer.companyName}
                  customerNumber={values.customer.customerNumber}
                  linked={Boolean(values.customer.sourceCustomerId)}
                  onTake={(customer) => takeFrom(customer, null)}
                  onShowAll={() => setPicking(true)}
                />
              </Field>
            </FullWidth>
            <Field id="company-customerNumber" label={t('leadForm.customerNumber')}>
              <Input {...companyField('customerNumber')} />
            </Field>
            <Field id="company-website" label={t('leadForm.website')}>
              <Input {...companyField('website')} />
            </Field>
            <Field id="company-phone" label={t('leadForm.companyPhone')}>
              <Input type="tel" {...companyField('phone')} />
            </Field>
            <Field id="company-email" label={t('leadForm.companyEmail')}>
              <Input type="email" {...companyField('email')} />
            </Field>

            {lead && (
              <Field
                id="lead-assigned-to"
                label={t('leadForm.assignedTo')}
                hint={mayAssign ? undefined : t('leadForm.assignedToHint')}
              >
                <div className="flex h-8 min-w-0 items-center gap-2">
                  {/* An <output> because a label can name it, and the name is a result, not a field. */}
                  <output
                    id="lead-assigned-to"
                    className="min-w-0 flex-1 text-sm"
                    aria-describedby={mayAssign ? undefined : 'lead-assigned-to-hint'}
                  >
                    <AssigneeName
                      assigneeId={assigneeId}
                      colleagues={directory.colleagues}
                      loaded={directory.loaded}
                    />
                  </output>
                  {mayAssign && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setAssigning(true)}>
                      {t('leads.actions.assign')}
                    </Button>
                  )}
                </div>
              </Field>
            )}
          </FieldGrid>
        </FormSection>

        <FormSection title={t('leadForm.addressSection')}>
          <FieldGrid>
            <FullWidth>
              <Field id="address-street" label={t('leadForm.street')}>
                <Input {...addressField('street')} />
              </Field>
            </FullWidth>
            {/* The postcode next to the town inside one half of the grid, so the outer column edge
                stays where it is in every other block. */}
            <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-4">
              <Field id="address-postalCode" label={t('leadForm.postalCode')}>
                <Input {...addressField('postalCode')} />
              </Field>
              <Field id="address-city" label={t('leadForm.city')}>
                <Input {...addressField('city')} />
              </Field>
            </div>
            <Field id="address-country" label={t('leadForm.country')}>
              <CountrySelect
                id="address-country"
                value={values.customer.address.country}
                onChange={(country) =>
                  update((current) => ({
                    customer: { ...current.customer, address: { ...current.customer.address, country } },
                  }))
                }
              />
            </Field>
          </FieldGrid>
        </FormSection>

        {/* One person, and no way to add a second. A lead is the conversation that was had at the
            stand, and that conversation was with somebody — a second name belongs on a lead of its
            own. The backend still carries a list, so a lead that arrived with several (the iOS app,
            an import) shows all of them and each can still be taken off; only adding is gone. */}
        <FormSection
          title={t('leadForm.contactsSection')}
          description={t('leadForm.contactsSectionHint')}
        >
          {values.contacts.map((contact, index) => {
            // Numbered only while there is more than one to tell apart; on the ordinary lead the
            // section heading above already says what this card is.
            const heading =
              values.contacts.length > 1
                ? t('leadForm.contactHeading', { index: index + 1 })
                : t('leadForm.contactsSection')

            return (
              <div
                key={contact.id ?? `new-${index}`}
                className="grid gap-4 rounded-lg border px-4 py-4"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                    {heading}
                  </span>
                  {/* Nothing to remove while there is one never-saved contact: typing over it is how
                      it is cleared, and a button that does nothing is worse than none. */}
                  {!singleEmptyContact && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      // Several cards mean several buttons reading "Entfernen". Which one is only
                      // obvious to somebody who can see where it sits.
                      aria-label={t('leadForm.removeContactLabel', {
                        name: contact.lastName.trim() || heading,
                      })}
                      onClick={() => removeContact(index)}
                    >
                      <Trash2Icon />
                      {t('leadForm.removeContact')}
                    </Button>
                  )}
                </div>

                <ContactFields
                  prefix={`contact-${index}`}
                  contact={contact}
                  lastNameError={errors.contacts?.[index]}
                  onChange={(next) => setContact(index, next)}
                />

                <div className="grid gap-2 border-t pt-4">
                  <span className="text-sm font-medium">{t('businessCards.section')}</span>
                  <BusinessCardStrip
                    leadId={lead?.id ?? null}
                    contactId={contact.id ?? null}
                    reloadToken={cardEpoch}
                    pending={pendingCards[index] ?? NO_PENDING_CARDS}
                    onChange={(next) =>
                      setPendingCards((current) => ({ ...current, [index]: next }))
                    }
                  />
                </div>
              </div>
            )
          })}
        </FormSection>

        <FormSection
          title={t('leadForm.interestsSection')}
          description={t('leadForm.interestsSectionHint')}
        >
          <InterestPicker
            categories={catalog.categories}
            loading={catalog.loading}
            selected={new Set(values.interestIds)}
            alsoShow={lead?.interests ?? []}
            onToggle={(id, checked) =>
              update((current) => ({
                interestIds: checked
                  ? [...current.interestIds, id].sort()
                  : current.interestIds.filter((existing) => existing !== id),
              }))
            }
          />
        </FormSection>

        <FormSection
          title={t('leadForm.summarySection')}
          description={t('leadForm.summarySectionHint')}
        >
          <Field id="lead-summary" label={t('leadForm.summary')} hint={t('leadForm.summaryHint')}>
            <Textarea
              id="lead-summary"
              rows={4}
              value={values.summary}
              onChange={(event) => patch({ summary: event.target.value })}
            />
          </Field>
        </FormSection>

        {/* Only on a lead that exists: before the first save there is no id to hang a note on,
            and what was said at the stand belongs in the summary above. */}
        {lead && (
          <FormSection title={t('leadForm.notesSection')} description={t('leadForm.notesSectionHint')}>
            <LeadNotes leadId={lead.id} notes={lead.notes} />
          </FormSection>
        )}
      </div>

      <DialogFooter className="m-0 shrink-0 rounded-b-xl sm:items-center sm:justify-between">
        {/* Says what the guard on the close button is about, before somebody runs into it. */}
        <p className="text-muted-foreground text-xs sm:mr-auto" aria-live="polite">
          {changed ? t('leadForm.unsaved') : null}
        </p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="outline" disabled={submitting} onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? t('leadForm.saving') : lead ? t('leadForm.save') : t('leadForm.create')}
          </Button>
        </div>
      </DialogFooter>

      <CustomerPicker open={picking} onOpenChange={setPicking} onPick={takeFrom} />


      <AssignLeadDialog
        // The company as saved: the dialog speaks of the lead as it stands, not of a name still
        // being typed over.
        lead={
          assigning && lead
            ? { id: lead.id, assigneeId, companyName: lead.customer.companyName }
            : null
        }
        onOpenChange={(open) => !open && setAssigning(false)}
        onAssigned={(next) => {
          setAssigneeId(next)
          onAssigned()
        }}
      />

      <AlertDialog open={removing !== null} onOpenChange={(open) => !open && setRemoving(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('leadForm.removeContactTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('leadForm.removeContactDescription', {
                name:
                  removing !== null
                    ? values.contacts[removing].lastName || t('leadForm.thisContact')
                    : '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => removing !== null && drop(removing)}
            >
              {t('leadForm.removeContact')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  )
}
